<template>
<div class="shopping">
    <div class="">
        <div class="product-name">{{ product.title }}</div>
    </div>
    <div class="steam-info">
        <div class="steam-users">
            <p class="steam-code text">OUT ON <b>STEAM: </b></p>
            <div class="storefronts">
                <PlatformIcon platform="steam" :classes="['steam-logo']" alt="Steam" />
                <PlatformIcon platform="windows" :classes="['windows-logo']" alt="Windows" />
                <PlatformIcon platform="apple" :classes="['apple-logo']" alt="Apple" />
            </div>
        </div>
    </div>
    <div class="buttons">
        <AddToCartBannerButton :shopify-product="shopifyProduct" :pack="product" />
        <NuxtLink :to="navigateToDetails(product)" role="button">
            <div class="all-storefronts">
                {{ $t('PROMOTIONS.MORE_STOREFRONTS') }}
                <span>
                    <i class="fa-solid fa-arrow-right arrow" />
                </span>
            </div>
        </NuxtLink>
    </div>
</div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import type { Shopify } from '@jackboxgames/shopify'
import type { JBGWeb } from '$types/JBGWeb'
import AddToCartBannerButton from '$components/shopping/promotions/AddToCartBannerButton.vue'
import PlatformIcon from '~/public/images/icons/platforms/PlatformIcon.vue'

const props = defineProps({
    promotion: Object as PropType<JBGWeb.ProductCta>,
    shopifyProduct: Object as PropType<Shopify.Product>
})

const localeRoute = useLocaleRoute()

const productType = computed(() => {
    if (props.promotion.packDetail?.data) return 'pack'
    if (props.promotion.gameDetail?.data) return 'game'
    if (props.promotion.bundleDetail?.data) return 'bundle'
    return null
})

const product = computed(() => {
    switch (productType.value) {
    case 'pack':
        return props.promotion.packDetail.data.attributes
    case 'game':
        return props.promotion.gameDetail.data.attributes
    case 'bundle':
        return props.promotion.bundleDetail.data.attributes
    default:
        return null
    }
})

function navigateToDetails(product: JBGWeb.PackDetail | JBGWeb.GameDetail | JBGWeb.BundleDetail) {
    switch (productType.value) {
    case 'pack':
        return localeRoute({
            name: 'games-packs-packslug',
            params: {
                packslug: product.slug
            }
        })
    case 'game':
        return localeRoute({
            name: 'games-gameslug',
            params: {
                gameslug: product.slug
            }
        })
    case 'bundle':
        return localeRoute({
            name: 'games-bundles-bundleslug',
            params: {
                bundleslug: product.slug
            }
        })
    default:
        return null
    }
}

</script>

<style scoped lang="scss">
@use "$styles/kit.scss" as *;

.shopping {
    flex-grow: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    margin-left: 140px;
    margin-right: 12px;

    @media screen and (min-width: 350px) and (max-width: 439px){
        margin-left: 114px;
    }

    @media screen and (min-width: 200px) and (max-width: 350px){
        margin-left: 98px;
    }

    .product-name {
        margin-top: 12px;
        margin-bottom: 6px;
        font-weight: 700;
        font-size: 15px;
        line-height: 15px;

        @media screen and (min-width: 200px) and (max-width: 439px){
            margin-top: 10px;
            margin-bottom: 2px;
            font-size: 13px;
        }
    }

    .steam-info {
        display: flex;
        width: 100%;

        .steam-users {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-direction: row;
            width: 100%;

            .text {
                margin-bottom: 0px;
                font-size: 12px;
                @media screen and (min-width: 200px) and (max-width: 439px){
                    font-size: 11px;
                    line-height: 1;
                }
            }

            .storefronts {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 5px;
            }
        }
    }

    .buttons {
        .all-storefronts {
            background-color: #3A424D;
            border-radius: 4px;
            width: 100%;
            max-width: 252px;
            height: 40px;
            display: flex;
            padding: 12px;
            align-items: center;
            justify-content: space-between;
            font-size: 13px;
            line-height: 1;
            font-weight: 400;
            color: var(--neutral-50);
            margin-bottom: 8px;

            @media screen and (min-width: 200px) and (max-width: 439px){
                margin-right: 8px;
                min-width: 100px;
                height: 32px;
                font-size: 12px;
            }

            .arrow {
                height: 16px;
                width: 16px;
            }

            &:hover {
                border: 1px solid var(--neutral-50);
                padding: 11px;
            }
        }
    }
}

:deep(.steam-logo) {
  height: 12px;
}

:deep(.apple-logo) {
  height: 14px;
}

:deep(.windows-logo) {
  height: 11px;
}

</style>
